import { useMutation, useQuery } from '@apollo/client';
import {
  TabsContainer,
  Icon,
  TabContent,
  getTranslatedText,
  toast,
  ERROR_CODES,
  BackgroundIcon,
  AppContext,
  isAuthorised,
  SideConversations,
  NewSideConversation,
  sideConversationPricingPlans,
  useIsAuthorisedHook,
  useCheckFeatureGating,
  getConversationOrder,
  COMMON_CONSTANTS
} from '@nile/components';
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import {
  ADD_NOTE_WORKITEM,
  AccountClient,
  DELETE_NOTE_WORKITEM,
  GET_NOTE_LIST,
  GET_VENDOR_CONVERSATION_LIST,
  TicketClient,
  UPDATE_NOTE_WORKITEM,
  useCachedQuery
} from '@nile/graphql';
import ProjectContext from '@nile/projects/src/Contexts/ProjectContext';
import {
  GET_PROJECT_CONVERSATIONS_LIST,
  GET_PROJECT_FEED_LIST,
  GET_PROJECT_CONVERSATIONS,
  GET_PROJECT_FEEDS,
  ADD_PROJECT_COMMENT
} from '@nile/projects/src/Graphql';
import { NotesContainer } from '@nile/mangifera/src/Containers/ChatContainers';
import { Newspaper, Comment } from '@styled-icons/fa-solid';
import WorklogDetails from '@nile/mangifera/src/Containers/PageContainers/WorklogDetails';
import {
  ProjectConversationsContainer,
  ProjectFeedsContainer
} from '@nile/projects/src/Containers';
import envConfig from '@nile/home/src/Config/env';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { GET_ROLE_PERMISSON } from '@nile/banyan/src/Graphql/Mutations/role';
import { cloneDeep } from 'lodash';
import { useGlobalState } from '@nile/components/src/Managers/StateManager';

const ProjectCommunicationContainer = ({ intl, project, setProject }) => {
  const flags = useFlags();
  const context = useContext(TicketContext);
  const [allFeeds, setAllFeeds] = useState([]);
  const [allnotes, setAllNotes] = useState([]);
  const [allConversations, setAllConversations] = useState([]);
  const [activeTabIndex, setActiveTabIndex] = useGlobalState(
    'projectActiveTabIndex',
    0
  );
  const [sideConversationToBeOpened, setSideConversationToBeOpened] = useState(
    null
  );
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user || {};
  const planId = currentUser?.chargeBeePlanId?.toLowerCase();
  const projectContext = useContext(ProjectContext);
  const [roleFeaturesList, setRoleFeaturesList] = useState(
    appContext?.user?.role?.roleFeatureList || []
  );
  const showSideConversation = useCheckFeatureGating('SIDE_CONVERSATIONS')
    ?.enabled;

  // value from user preferred conversation order
  const conversationOrder = getConversationOrder() || 'ASC';

  const roleId = project?.loggedInUserRole?.roleId;
  useQuery(GET_ROLE_PERMISSON, {
    variables: { roleId: roleId },
    client: AccountClient,
    skip: !roleId,
    onCompleted({ getRolePermissions }) {
      const updatedFeatures = cloneDeep(roleFeaturesList);
      if (getRolePermissions?.roleFeatureList?.length) {
        getRolePermissions.roleFeatureList.forEach((role) => {
          const idx = updatedFeatures.findIndex(
            (feat) => feat.feature.key === role.feature.key
          );
          if (idx >= 0) {
            updatedFeatures[idx] = role;
          } else {
            updatedFeatures.push(role);
          }
        });
      }
      setRoleFeaturesList(updatedFeatures);
    }
  });

  const isEditAuthorised = isAuthorised(roleFeaturesList, 'comments.add');

  const [editNoteId, setEditNoteId] = useState(null);
  const editNoteIdByRef = useRef(null);
  editNoteIdByRef.current = editNoteId;

  const workItem = {
    workItem: { workId: project?.projectId, module: 'PROJECT' }
  };

  const conversationInputVariable = {
    input: {
      project: {
        projectId: project.projectId
      },
      listInfo: {
        sort: [
          {
            attribute: 'addedOn',
            order: conversationOrder
          }
        ]
      }
    }
  };

  const feedsInputVariable = {
    input: {
      project: {
        projectId: project.projectId
      },
      listInfo: {
        sort: [
          {
            attribute: 'time',
            order: conversationOrder
          }
        ]
      }
    }
  };

  const { loading: feedsLoading, error, data } = useQuery(
    GET_PROJECT_FEED_LIST,
    {
      client: TicketClient,
      variables: feedsInputVariable,
      fetchPolicy: 'network-only',
      onCompleted(newData) {
        if (newData?.getProjectFeedList?.projectFeeds) {
          setAllFeeds(newData?.getProjectFeedList?.projectFeeds);
        }
      }
    }
  );

  const {
    loading: notesLoading,
    error: notesError,
    data: notesData
  } = useQuery(GET_NOTE_LIST, {
    variables: {
      input: workItem
    },
    fetchPolicy: 'network-only',
    onCompleted(newData) {
      if (newData?.getNoteList) {
        setAllNotes(newData.getNoteList);
      }
    }
  });

  const {
    loading: conversationsLoading,
    error: conversationsError,
    data: conversationsData
  } = useQuery(GET_PROJECT_CONVERSATIONS_LIST, {
    client: TicketClient,
    variables: conversationInputVariable,
    fetchPolicy: 'network-only',
    onCompleted(newData) {
      if (newData?.getProjectConversationList?.projectConversations) {
        setAllConversations(
          newData?.getProjectConversationList?.projectConversations
        );
      }
    }
  });

  const readFeedsCache = (cache) => {
    return cache.readQuery({
      query: GET_PROJECT_FEED_LIST,
      variables: feedsInputVariable
    });
  };

  const writeFeedsCache = (cache, feeds) => {
    cache.writeQuery({
      query: GET_PROJECT_FEED_LIST,
      variables: feedsInputVariable,
      data: {
        getProjectFeedList: {
          projectFeeds: feeds
        }
      }
    });
  };

  const readNoteCache = (cache) => {
    return cache.readQuery({
      query: GET_NOTE_LIST,
      variables: { input: workItem }
    });
  };

  const writeNoteCache = (cache, notes) => {
    cache.writeQuery({
      query: GET_NOTE_LIST,
      variables: { input: workItem },
      data: {
        getNoteList: notes
      }
    });
  };

  const arrangeListOrder = (list, updatedList) => {
    // append new feed/notes/conversation data top or bottom based on conversationOrder
    let returnList = [];
    if (conversationOrder === 'DESC') {
      returnList = [updatedList, ...list];
    } else if (conversationOrder === 'ASC') {
      returnList = [...list, updatedList];
    }
    return returnList;
  };

  const [saveProjectComment] = useMutation(ADD_PROJECT_COMMENT, {
    onCompleted({ addProjectComment }) {
      toast('success', 'Comment added successfully');
    },
    update(cache, { data }) {
      const feedCache = readFeedsCache(cache);
      const conversationsCache = cache.readQuery({
        query: GET_PROJECT_CONVERSATIONS_LIST,
        variables: conversationInputVariable
      });
      const updatedFeed = {
        approval: null,
        conversation: data.addProjectComment,
        note: null,
        task: null,
        type: 'conversation'
      };
      const updatedConversation = data.addProjectComment;
      let feeds = [...feedCache?.getProjectFeedList?.projectFeeds];
      let conversations = [
        ...conversationsCache?.getProjectConversationList?.projectConversations
      ];
      conversations = arrangeListOrder(conversations, updatedConversation);
      feeds = arrangeListOrder(feeds, updatedFeed);
      writeFeedsCache(cache, feeds);
      cache.writeQuery({
        query: GET_PROJECT_CONVERSATIONS_LIST,
        variables: conversationInputVariable,
        data: {
          getProjectConversationList: {
            projectConversations: conversations
          }
        }
      });
      setAllFeeds(feeds);
      setAllConversations(conversations);
    },
    onError({ graphQLErrors }) {}
  });

  const [saveProjectNote] = useMutation(ADD_NOTE_WORKITEM, {
    onCompleted({ createNote }) {
      toast('success', 'ticket.updates.newNoteCreate.success');
    },
    update(cache, { data }) {
      const feedCache = readFeedsCache(cache);
      const notesCache = readNoteCache(cache);
      const updatedFeed = {
        type: 'note',
        conversation: null,
        note: data.createNote,
        task: null,
        approval: null
      };
      const updatedNote = data.createNote;
      let feeds = [...feedCache?.getProjectFeedList?.projectFeeds];
      let notes = [...notesCache.getNoteList];
      feeds = arrangeListOrder(feeds, updatedFeed);
      notes = arrangeListOrder(notes, updatedNote);
      writeFeedsCache(cache, feeds);
      writeNoteCache(cache, notes);

      setAllFeeds(feeds);
      setAllNotes(notes);
    },
    onError({ graphQLErrors }) {}
  });

  const updateVendorConversation = (data) => {
    const feedCache = AccountClient.readQuery({
      query: GET_PROJECT_FEED_LIST,
      variables: feedsInputVariable
    });
    const updatedFeed = {
      type: 'VENDOR_CONVERSATION',
      conversation: null,
      note: null,
      task: null,
      approval: null,
      vendorConversation: data
    };
    let feeds = [...feedCache?.getProjectFeedList?.projectFeeds];
    feeds = arrangeListOrder(feeds, updatedFeed);
    AccountClient.writeQuery({
      query: GET_PROJECT_FEED_LIST,
      variables: feedsInputVariable,
      data: {
        getProjectFeedList: {
          projectFeeds: feeds
        }
      }
    });
    setAllFeeds(feeds);
  };

  const [vendorConversations, setVendorConversations] = useState(null);
  const {
    data: sideConversationData,
    loading: sideConversationLoading,
    error: sideConversationError,
    refetch: sideConversationRefetch
  } = useQuery(GET_VENDOR_CONVERSATION_LIST, {
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    variables: {
      input: {
        module: 'PROJECT',
        listInfo: {
          condition: {
            joinOperator: 'AND',
            operands: [
              {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'parentConversationId',
                    operator: 'isEmpty'
                  }
                ]
              },
              {
                joinOperator: 'AND',
                operands: [
                  {
                    attribute: 'workItem.workId',
                    operator: 'is',
                    value: project.projectId
                  }
                ]
              }
            ]
          },
          sort: [
            {
              attribute: 'time',
              order: conversationOrder
            }
          ]
        }
      }
    }
  });

  const actions = {
    saveProjectComment,
    saveProjectNote: saveProjectNote
  };

  const [
    deleteNote,
    { loading: deleteNoteLoading, error: deleteNoteError }
  ] = useMutation(DELETE_NOTE_WORKITEM, {
    onCompleted({ res }) {
      toast('success', getTranslatedText(intl, 'ticket.note.delete.success'));
    },
    update(cache, { data }) {
      toast('success', getTranslatedText(intl, 'ticket.note.delete.success'));
      const deletedNoteId = data?.deleteNote;
      const noteCache = readNoteCache(cache);
      const feedsCache = readFeedsCache(cache);
      const notes = [...noteCache.getNoteList];
      const feeds = cloneDeep(feedsCache?.getProjectFeedList?.projectFeeds);
      const updatedNotes = notes.filter((el) => el.noteId !== deletedNoteId);
      const updatedFeeds = feeds.filter(
        (el) => el?.note?.noteId !== deletedNoteId
      );
      writeNoteCache(cache, updatedNotes);
      writeFeedsCache(cache, updatedFeeds);
      setAllFeeds(updatedFeeds);
    }
  });

  const [
    editNote,
    { loading: editNoteLoading, error: editNoteError }
  ] = useMutation(UPDATE_NOTE_WORKITEM, {
    onCompleted({ res }) {
      // toast('success', getTranslatedText(intl, 'ticket.note.edit.success'));
      setEditNoteId(null);
    },
    update(cache, { data }) {
      const updateNote = data?.updateNote;
      const noteCache = readNoteCache(cache);
      const feedsCache = readFeedsCache(cache);
      const notes = cloneDeep(noteCache.getNoteList);
      const feeds = cloneDeep(feedsCache?.getProjectFeedList?.projectFeeds);
      const updatedNotes = notes.map((el) => {
        if (el.noteId === updateNote.noteId) {
          return {
            ...updateNote
          };
        }
        return el;
      });
      const updatedFeeds = feeds.map((el) => {
        if (
          el.type === 'note' &&
          el.type === 'NOTE' &&
          el.note?.noteId === updateNote.noteId
        ) {
          return {
            ...el,
            note: updateNote
          };
        }
        return el;
      });
      writeNoteCache(cache, updatedNotes);
      writeFeedsCache(cache, updatedFeeds);
      setAllFeeds(updatedFeeds);
    },
    onError() {
      // console.log(err)
    }
  });

  const sideConversationLength =
    sideConversationData?.getVendorConversationsList?.conversations?.length;
  useEffect(() => {
    let vendorSideConversation =
      sideConversationData?.getVendorConversationsList?.conversations || [];
    if (vendorSideConversation?.length > 0) {
      setVendorConversations(vendorSideConversation);
      if (conversationOrder === 'ASC') {
        //set conversationId in state to open latest conversation
        let openedConversationId =
          vendorSideConversation[vendorSideConversation.length - 1]
            ?.conversationId;
        openedConversationId &&
          setSideConversationToBeOpened(openedConversationId);
      }
    }
  }, [sideConversationLength]);

  const updateNote = ({ variables }) => {
    const { input } = variables;
    return editNote({
      variables: {
        input: {
          ...input,
          ...workItem
        }
      }
    });
  };

  const destroyNote = ({ variables }) => {
    const { input } = variables;
    deleteNote({
      variables: {
        input: {
          ...input,
          ...workItem
        }
      }
    });
  };

  const switchToConnect = (dats) => {
    const convoId = dats?.parentConversationId || dats?.conversationId;
    if (convoId && sideConversationToBeOpened !== convoId)
      setSideConversationToBeOpened(convoId);

    const ConnectTabIndex = authorisedTabs.findIndex((tab) => {
      return tab.id === 'connect';
    });
    if (ConnectTabIndex) {
      setActiveTabIndex(ConnectTabIndex);
    }
  };

  const createNewSideConversation = () => {
    function onSubmit(data) {
      sideConversationRefetch();
      switchToConnect(data);
    }
    return (
      <NewSideConversation
        plain
        intl={intl}
        workItemId={project.projectId}
        module="PROJECT"
        updateCache={updateVendorConversation}
        onSubmit={onSubmit}
        backgroundThreadConversation={flags?.sideConversationBackgroundThread}
      />
    );
  };

  const getTabContent = (t, suffix) => {
    switch (t) {
      case 'Activities':
        return (
          <TabContent
            content={getTranslatedText(
              intl,
              'project.details.navbar.activities'
            )}
          />
        );
      case 'Conversation':
        return (
          <TabContent
            content={getTranslatedText(
              intl,
              'project.details.navbar.conversation'
            )}
          />
        );
      case 'Worklog':
        return (
          <TabContent
            content={getTranslatedText(intl, 'project.details.navbar.worklog')}
          />
        );
      default:
        return t;
    }
  };

  const tabs = [
    {
      id: 'activities',
      content: getTranslatedText(intl, 'project.details.navbar.activities'),
      iconName: 'Updates',
      accessibilityLabel: 'Activities',
      panelID: 'all-activities',
      component: (
        <ProjectFeedsContainer
          feeds={allFeeds}
          loading={feedsLoading}
          actions={actions}
          project={project}
          setProject={setProject}
          switchToConnect={switchToConnect}
          createNewSideConversation={
            showSideConversation &&
            isEditAuthorised &&
            createNewSideConversation
          }
          editNoteId={editNoteIdByRef.current}
          setEditNoteId={setEditNoteId}
          deleteNote={destroyNote}
          editNote={updateNote}
          editNoteLoading={editNoteLoading}
        />
      )
    },
    {
      id: 'conversation',
      content: (
        <TabContent
          content={`${getTranslatedText(
            intl,
            'project.details.navbar.conversation'
          )}`}
          // icon={<SVGIcon name="helpcenter" />}
          count={allConversations.length || 0}
        />
      ),
      // content: getTranslatedText(intl, 'project.details.navbar.conversation'),
      iconName: 'Conversation',
      accessibilityLabel: 'Conversation',
      panelID: 'all-conversations',
      component: (
        <ProjectConversationsContainer
          conversations={allConversations}
          loading={conversationsLoading}
          actions={actions}
          project={project}
        />
      )
    },
    {
      id: 'notes',
      content: (
        <TabContent
          // content={`${getTranslatedText(intl, 'project.details.navbar.notes')}`}
          content={`${getTranslatedText(intl, 'notes')}`}
          // icon={<SVGIcon name="helpcenter" />}
          count={allnotes.length || 0}
        />
      ),
      // content: getTranslatedText(intl, 'project.details.navbar.conversation'),
      iconName: 'Notes',
      accessibilityLabel: 'Notes',
      panelID: 'all-notes',
      component: (
        <NotesContainer
          module="PROJECT"
          workItemId={project.projectId}
          placeholder="project.details.feed.placeholder"
          disableEdit={!isAuthorised(projectContext.features, 'comments.add')}
          allowEditDeleteNotes
          disableSpit
          emptyStateTitle="workItem.notes.emptyState.title"
          emptyStateDescription="project.notes.emptyState.description"
        />
      )
    },
    {
      id: 'connect',
      content: (
        <TabContent
          content={`${getTranslatedText(
            intl,
            'app.common.sideConversation.title'
          )}`}
          // icon={<SVGIcon name="helpcenter" />}
          count={vendorConversations?.length || 0}
        />
      ),
      // content: getTranslatedText(intl, 'app.common.sideConversation.title'),
      iconName: 'Helpcenter',
      accessibilityLabel: 'SideConversation',
      // authorisation: 'tickets.edit',
      panelID: 'all-sideConversation',
      pricingPlans: sideConversationPricingPlans,
      featureGateName: 'SIDE_CONVERSATIONS',
      component: (
        <SideConversations
          module="PROJECT"
          workItemId={project.projectId}
          refetch={sideConversationRefetch}
          updateFeedsCache={updateVendorConversation}
          createNewSideConversation={
            showSideConversation &&
            isEditAuthorised &&
            createNewSideConversation
          }
          vendorConversations={vendorConversations}
          conversationToBeOpened={sideConversationToBeOpened}
          emptyStateData={{
            description:
              'project.details.sideConversation.emptyState.description'
          }}
          backgroundThreadConversation={flags?.sideConversationBackgroundThread}
          preventCacheUpdate
        />
      )
    }
  ].filter(({ pricingPlans, featureGateName }) => {
    if (featureGateName) {
      return useCheckFeatureGating(featureGateName)?.enabled;
    }
    return true;
  });

  let authorisedTabs = tabs.filter((el) => {
    if (!el.authorisation) return true;
    const isFeatureAuthorised = isAuthorised(
      roleFeaturesList,
      `${el.authorisation}`
    );
    return isFeatureAuthorised;
  });

  if (currentUser.accountType !== 'MSP') {
    authorisedTabs = authorisedTabs.filter((el) => el.id !== 'connect');
  }

  const switchTabIndex = (idx) => {
    setActiveTabIndex(idx);
  };

  return (
    <TabsContainer
      onTabChange={switchTabIndex}
      activeTabIndex={activeTabIndex}
      tabs={authorisedTabs}
    />
  );
};

export default injectIntl(ProjectCommunicationContainer);
